import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Renders HTML that arrived from outside the building, inertly.
 *
 * TECH_DEBT #34. The two email panes each carried a copy of a
 * `sanitizeEmailHtml` regex chain and fed the result to
 * `dangerouslySetInnerHTML`. It stripped `<script>` blocks and QUOTED event
 * handlers, and nothing else. Run against the obvious payloads, five of six came
 * back byte-identical:
 *
 *     <img src=x onerror=alert(1)>          unquoted attribute, no match
 *     <img/onerror=alert(1) src=x>          slash separator, no leading \s
 *     <svg onload=alert(1)>                 same
 *     <body onload=alert(1)>                same
 *     <a href="javas&#99;ript:alert(1)">    entity-encoded scheme
 *
 * The input is INBOUND EMAIL, so the attacker needs no account and no access:
 * anyone who knows an ingested inbox address can send crafted HTML that then
 * executes in the authenticated app origin the moment a staff member opens the
 * message. That is why this is not a patch to the regex chain — the approach
 * cannot be patched into correctness, and a sanitizer that is 80% right is worse
 * than none because it reads as protection.
 *
 * So the markup is not cleaned at all. It is handed over verbatim to an iframe
 * that cannot execute anything:
 *
 *   - NO `allow-scripts`. This is the whole defence. Without it no inline
 *     handler, no `<script>`, no `javascript:` URL and no `srcset`/CSS trick
 *     runs, whatever the markup says — the browser enforces it, not a regex.
 *   - NO `allow-forms`. A phishing form in an email cannot post anywhere.
 *   - `allow-same-origin` IS set, and is safe ONLY because `allow-scripts` is
 *     not. It exists so the parent can measure `scrollHeight` and size the frame
 *     to its content, which is what keeps this a drop-in replacement for the
 *     inline div rather than a pane with its own scrollbar.
 *
 *     **`allow-scripts` + `allow-same-origin` together is a full sandbox
 *     escape** — the frame could then reach into the parent document and remove
 *     its own sandbox attribute. Either token alone is fine; the pair is not.
 *     `untrustedHtmlFrame.test.tsx` fails if anything ever adds the second one.
 *
 *   - `allow-popups allow-popups-to-escape-sandbox` so that a link a staff
 *     member deliberately clicks still opens, in a new tab. Combined with the
 *     injected `<base target="_blank">` below, a click cannot silently replace
 *     the message they are reading with an attacker's page.
 *
 * `srcDoc` rather than a blob: URL because React escapes the attribute for us
 * and there is no object URL to leak or revoke.
 */

/** Height used before the first measurement, and if measuring is refused. */
const INITIAL_HEIGHT_PX = 240;
const MAX_HEIGHT_PX = 2000;

type Props = {
  html: string;
  /** Describes the frame to a screen reader. */
  title: string;
  className?: string;
};

export const UntrustedHtmlFrame: React.FC<Props> = ({ html, title, className }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT_PX);

  const measure = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      // Reachable because allow-same-origin is set. Wrapped anyway: a browser
      // that refuses (or a document that never parsed) must leave the fallback
      // height standing rather than throw inside a load handler.
      const doc = frame.contentDocument;
      const measured = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 0;
      if (measured > 0) setHeight(Math.min(measured + 8, MAX_HEIGHT_PX));
    } catch {
      // Deliberately empty is NOT allowed in this codebase, so say what this is:
      // the height stays at its last good value and the frame scrolls itself.
      // There is nothing to report — no data is lost and nothing was written.
      setHeight((current) => current);
    }
  }, []);

  // Re-measure when the document changes, not only on load: switching messages
  // reuses the same iframe element, and a taller message in a shorter frame
  // would otherwise be clipped until something else forced a render.
  useLayoutEffect(() => {
    setHeight(INITIAL_HEIGHT_PX);
  }, [html]);

  // `<base target="_blank">` first, so it applies to every link that follows.
  // The charset declaration stops a message without one being decoded as the
  // parent's encoding and rendering as mojibake.
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">` +
    `<style>html,body{margin:0;padding:0;font:12px/1.6 system-ui,-apple-system,"Segoe UI",sans-serif;color:#334155;overflow-wrap:anywhere}` +
    `img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${html}</body></html>`;

  return (
    <iframe
      ref={frameRef}
      title={title}
      onLoad={measure}
      // The security boundary. Read the header comment before changing a token.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      className={className}
      style={{ width: "100%", height, border: 0, display: "block" }}
    />
  );
};

export default UntrustedHtmlFrame;
